import { Registro } from '../types';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const useExportData = () => {
  // Exportar a CSV
  const exportToCSV = (registros: Registro[], filename = 'registros.csv') => {
    if (registros.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Encabezados
    const headers = [
      'ID',
      'Fecha',
      'Lugar',
      'Incidencia',
      'Categoría',
      'Usuario',
      'Observaciones',
      'Cliente',
    ];

    // Convertir registros a filas CSV
    const rows = registros.map(r => [
      r.id,
      new Date(r.fecha).toLocaleString('es-ES'),
      r.lugar,
      r.incidencia || '',
      r.categoria || '',
      r.usuario || '',
      r.observaciones || '',
      r.cliente || '',
    ]);

    // Crear contenido CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    // Descargar archivo
    const blob = new Blob(['\ufeff' + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Exportar a Excel (usando CSV con extensión .xls)
  const exportToExcel = (registros: Registro[], filename = 'registros.xls') => {
    exportToCSV(registros, filename);
  };

  // Exportar a PDF
  const exportToPDF = async (registros: Registro[], filename = 'registros.pdf') => {
    if (registros.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF();

    // Logo
    const logoUrl = '/logo.png';
    let y = 15;
    try {
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      await new Promise(resolve => {
        reader.onloadend = resolve;
      });
      const imgData = reader.result as string;
      const img = new window.Image();
      img.src = imgData;
      await new Promise(resolve => {
        img.onload = resolve;
      });
      const aspectRatio = img.width / img.height;
      const logoHeight = 15;
      const logoWidth = logoHeight * aspectRatio;
      doc.addImage(imgData, 'PNG', 14, y, logoWidth, logoHeight);
    } catch (e) {
      console.error('Error al cargar logo:', e);
    }

    // Título
    doc.setFontSize(18);
    doc.setTextColor(33, 33, 33);
    doc.text('REGISTROS EXPORTADOS', 196, y + 10, { align: 'right' });
    doc.setDrawColor(0, 136, 254);
    doc.setLineWidth(1);
    doc.line(14, y + 15, 196, y + 15);

    // Información
    doc.setFontSize(12);
    doc.text(`Total de registros: ${registros.length}`, 14, y + 23);
    doc.text(`Fecha de exportación: ${new Date().toLocaleString('es-ES')}`, 14, y + 29);

    // Tabla
    const tableData = registros.map(r => [
      new Date(r.fecha).toLocaleDateString('es-ES'),
      r.lugar,
      r.incidencia || '',
      r.categoria || '',
      r.usuario || '',
    ]);

    autoTable(doc, {
      startY: y + 35,
      head: [['Fecha', 'Lugar', 'Incidencia', 'Categoría', 'Usuario']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 136, 254],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      alternateRowStyles: { fillColor: [240, 248, 255] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 40 },
        2: { cellWidth: 50 },
        3: { cellWidth: 35 },
        4: { cellWidth: 30 },
      },
      margin: { left: 14, right: 14 },
      didDrawPage: () => {
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(
          'Servicios Estratégicos de Protección y Vigilancia Integral S. A. (Seprovinsa Security)',
          14,
          pageHeight - 8
        );
        doc.text(
          `Generado: ${new Date().toLocaleString()}`,
          14,
          pageHeight - 3
        );
      },
    });

    doc.save(filename);
  };

  // Exportar a JSON
  const exportToJSON = (registros: Registro[], filename = 'registros.json') => {
    if (registros.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const jsonContent = JSON.stringify(registros, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    exportToCSV,
    exportToExcel,
    exportToPDF,
    exportToJSON,
  };
};
